import CallRecordsApi from 'dashboard/api/callRecords';

const syncTimers = {};

const state = {
  byConversation: {},
};

const getters = {
  getConversationState: $state => conversationId =>
    $state.byConversation[conversationId] || {
      status: 'none',
      expiresAt: null,
      grantedAt: null,
      loading: false,
      callState: 'idle',
    },
};

const mutations = {
  SET_LOADING(stateData, { conversationId, loading }) {
    const current = stateData.byConversation[conversationId] || {};
    stateData.byConversation = {
      ...stateData.byConversation,
      [conversationId]: {
        ...current,
        loading,
      },
    };
  },
  SET_PERMISSION_STATUS(stateData, { conversationId, status, expiresAt, grantedAt }) {
    const current = stateData.byConversation[conversationId] || {};
    stateData.byConversation = {
      ...stateData.byConversation,
      [conversationId]: {
        ...current,
        status,
        expiresAt,
        grantedAt,
      },
    };
  },
  SET_CALL_STATE(stateData, { conversationId, callState }) {
    const current = stateData.byConversation[conversationId] || {};
    stateData.byConversation = {
      ...stateData.byConversation,
      [conversationId]: {
        ...current,
        callState,
      },
    };
  },
};

const actions = {
  QUEUE_PERMISSION_SYNC({ dispatch }, { conversationId, delay = 350 }) {
    if (!conversationId) return;
    if (syncTimers[conversationId]) {
      clearTimeout(syncTimers[conversationId]);
    }

    syncTimers[conversationId] = setTimeout(() => {
      dispatch('FETCH_PERMISSION_STATUS', { conversationId }).catch(() => {});
      delete syncTimers[conversationId];
    }, delay);
  },
  async FETCH_PERMISSION_STATUS({ commit }, { conversationId }) {
    commit('SET_LOADING', { conversationId, loading: true });
    try {
      const { data } = await CallRecordsApi.permissionStatus(conversationId);
      commit('SET_PERMISSION_STATUS', {
        conversationId,
        status: data.status || 'none',
        expiresAt: data.expires_at || null,
        grantedAt: data.granted_at || null,
      });
      return data;
    } finally {
      commit('SET_LOADING', { conversationId, loading: false });
    }
  },
  async REQUEST_CALL_PERMISSION({ commit }, { conversationId, payload = {} }) {
    commit('SET_LOADING', { conversationId, loading: true });
    try {
      const { data } = await CallRecordsApi.requestPermission(conversationId, payload);
      commit('SET_PERMISSION_STATUS', {
        conversationId,
        status: data.permission_status || 'pending',
        expiresAt: data.permission_expires_at || null,
        grantedAt: null,
      });
      return data;
    } finally {
      commit('SET_LOADING', { conversationId, loading: false });
    }
  },
  async INITIATE_WHATSAPP_CALL({ commit, dispatch, rootState }, { conversationId, payload = {} }) {
    commit('SET_CALL_STATE', { conversationId, callState: 'dialing' });
    try {
      const { data } = await CallRecordsApi.initiate(conversationId, payload);
      // eslint-disable-next-line no-console
      console.info('[WSP_CALL_TRACE] initiate:set_terminate_context', {
        conversationId,
        whatsappCallDbId: data.whatsapp_call_id,
      });
      commit('telephony/SET_INTERNAL_WHATSAPP_CALL_CONTEXT', {
        whatsappCallDbId: data.whatsapp_call_id,
        whatsappCallConversationId: conversationId,
      }, { root: true });

      const phoneNumber = (payload.phoneNumber || '').toString().trim();
      if (phoneNumber) {
        const inboxId = data.inbox_id || payload.inboxId || null;
        await dispatch(
          'telephony/PREPARE_DIAL',
          {
            phoneNumber,
            contactId: payload.contactId || null,
            conversationDisplayId: conversationId,
            callType: 'whatsapp',
            mediaRoute: inboxId ? { type: 'whatsapp', inboxId } : null,
          },
          { root: true }
        );

        // Si no requiere selección de inbox SIP, marcar de inmediato.
        if (!rootState?.telephony?.outboundNeedsInboxSelection) {
          await dispatch('telephony/CONFIRM_DIAL', null, { root: true });
        }
      }
      commit('SET_CALL_STATE', { conversationId, callState: 'initiated' });
      return data;
    } catch (error) {
      commit('SET_CALL_STATE', { conversationId, callState: 'error' });
      throw error;
    }
  },
  async TERMINATE_WHATSAPP_CALL({ commit, dispatch }, { conversationId, payload = {} }) {
    const { data } = await CallRecordsApi.terminate(conversationId, payload);
    dispatch('telephony/HANGUP', null, { root: true });
    commit('SET_CALL_STATE', { conversationId, callState: 'terminated' });
    return data;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
